import { type FormEvent, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowRight, CheckCircle2, Clock, Hourglass, Upload } from "lucide-react";
import { toast } from "sonner";
import { api, ApiError } from "@/lib/api";
import type { Service, ServiceField } from "@/lib/types";
import { money } from "@/lib/format";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { Textarea } from "@/components/ui/textarea";
import { useAuth } from "@/lib/auth";

function parseOptions(json: string | null): string[] {
  if (!json) return [];
  try { return JSON.parse(json) as string[]; } catch { return []; }
}

function FieldInput({
  field,
  value,
  onChange,
  onUpload,
}: {
  field: ServiceField;
  value: unknown;
  onChange: (v: unknown) => void;
  onUpload: (file: File, field: ServiceField) => Promise<void>;
}) {
  const opts = parseOptions(field.options_json);
  switch (field.kind) {
    case "textarea":
      return (
        <Textarea
          rows={4}
          value={(value as string) ?? ""}
          onChange={e => onChange(e.target.value)}
          required={!!field.required}
        />
      );
    case "number":
      return (
        <Input
          type="number"
          value={(value as string) ?? ""}
          onChange={e => onChange(e.target.value)}
          required={!!field.required}
        />
      );
    case "date":
      return (
        <Input
          type="date"
          value={(value as string) ?? ""}
          onChange={e => onChange(e.target.value)}
          required={!!field.required}
        />
      );
    case "boolean":
      return (
        <label className="flex items-center gap-2 text-sm">
          <Checkbox
            checked={!!value}
            onCheckedChange={v => onChange(!!v)}
          />
          <span>Yes</span>
        </label>
      );
    case "select":
      return (
        <Select
          value={(value as string) ?? ""}
          onValueChange={v => onChange(v)}
        >
          <SelectTrigger><SelectValue placeholder="Choose…" /></SelectTrigger>
          <SelectContent>
            {opts.map(o => <SelectItem key={o} value={o}>{o}</SelectItem>)}
          </SelectContent>
        </Select>
      );
    case "multiselect": {
      const arr = Array.isArray(value) ? (value as string[]) : [];
      return (
        <div className="flex flex-wrap gap-2">
          {opts.map(o => {
            const active = arr.includes(o);
            return (
              <button
                key={o}
                type="button"
                onClick={() =>
                  onChange(active ? arr.filter(x => x !== o) : [...arr, o])
                }
                className={[
                  "rounded-full border px-3 py-1 text-xs transition",
                  active
                    ? "border-foreground bg-foreground text-background"
                    : "border-border bg-background hover:bg-accent",
                ].join(" ")}
              >
                {o}
              </button>
            );
          })}
        </div>
      );
    }
    case "upload":
      return (
        <label className="flex cursor-pointer items-center gap-3 rounded-md border border-dashed p-3 text-sm text-muted-foreground hover:bg-accent">
          <Upload className="h-4 w-4" />
          <span>{Array.isArray(value) && value.length ? `${(value as unknown[]).length} file(s) attached` : "Click to upload references"}</span>
          <input
            type="file"
            className="hidden"
            multiple
            onChange={async e => {
              const files = Array.from(e.target.files ?? []);
              for (const f of files) await onUpload(f, field);
              e.target.value = "";
            }}
          />
        </label>
      );
    default:
      return (
        <Input
          value={(value as string) ?? ""}
          onChange={e => onChange(e.target.value)}
          required={!!field.required}
        />
      );
  }
}

export function RequestBuilderPage() {
  const { handle, slug } = useParams<{ handle: string; slug: string }>();
  const { user } = useAuth();
  const nav = useNavigate();
  const [service, setService] = useState<Service | null>(null);
  const [notFound, setNotFound] = useState(false);
  const [brief, setBrief] = useState<Record<string, unknown>>({});
  const [addons, setAddons] = useState<Set<string>>(new Set());
  const [deadline, setDeadline] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const res = await api.get<{ service: Service }>(
          `/api/creators/${handle}/services/${slug}`,
        );
        if (alive) setService(res.service);
      } catch {
        if (alive) setNotFound(true);
      }
    })();
    return () => { alive = false; };
  }, [handle, slug]);

  const livePrice = useMemo(() => {
    if (!service) return 0;
    let total = service.starting_price_cents;
    for (const a of service.addons ?? []) if (addons.has(a.id)) total += a.price_cents;
    return total;
  }, [service, addons]);

  const liveDeposit = useMemo(() => {
    if (!service) return 0;
    return Math.round((livePrice * service.deposit_percent) / 100);
  }, [service, livePrice]);

  async function uploadField(file: File, field: ServiceField) {
    try {
      const up = await api.upload(file, { kind: "reference", jobId: null });
      setBrief(b => {
        const prev = (b[field.label] as unknown[] | undefined) ?? [];
        return {
          ...b,
          [field.label]: [...prev, { id: up.id, filename: up.filename, mime: up.mime, url: up.url }],
        };
      });
    } catch (e) {
      toast.error(e instanceof ApiError ? e.message : "upload failed");
    }
  }

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (!service) return;
    if (!user) { nav(`/login?next=/${handle}/${slug}`); return; }
    setSubmitting(true);
    try {
      const res = await api.post<{ jobId: string }>("/api/jobs", {
        serviceId: service.id,
        brief,
        addonIds: Array.from(addons),
        deadlineRequested: deadline ? Math.floor(new Date(deadline).getTime() / 1000) : null,
      });
      toast.success("Request submitted");
      nav(`/jobs/${res.jobId}`);
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "submit failed");
    } finally {
      setSubmitting(false);
    }
  }

  if (notFound) {
    return <div className="rounded-lg border border-dashed p-12 text-center text-sm text-muted-foreground">Service not found.</div>;
  }
  if (!service) {
    return <Skeleton className="h-96 w-full" />;
  }

  const isInstant = service.quote_mode === "instant";

  return (
    <div className="grid gap-6 lg:grid-cols-[1fr_340px]">
      <form onSubmit={onSubmit} className="space-y-6">
        <div>
          <p className="mb-1 text-xs uppercase tracking-widest text-muted-foreground">
            Requesting from <Link to={`/${handle}`} className="underline underline-offset-4">@{handle}</Link>
          </p>
          <h1 className="text-3xl font-semibold tracking-tight">{service.title}</h1>
          {service.description && (
            <p className="mt-2 max-w-prose text-sm text-muted-foreground">{service.description}</p>
          )}
        </div>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base">Your brief</CardTitle>
          </CardHeader>
          <CardContent className="space-y-5">
            {(service.fields ?? []).length === 0 ? (
              <p className="text-sm text-muted-foreground">This creator hasn't added intake fields yet — tell them what you need in a short note below.</p>
            ) : (
              (service.fields ?? []).map(f => (
                <div key={f.id} className="space-y-1.5">
                  <Label>
                    {f.label}
                    {f.required ? <span className="ml-1 text-destructive">*</span> : null}
                  </Label>
                  {f.help_text && (
                    <p className="text-xs text-muted-foreground">{f.help_text}</p>
                  )}
                  <FieldInput
                    field={f}
                    value={brief[f.label]}
                    onChange={v => setBrief(b => ({ ...b, [f.label]: v }))}
                    onUpload={uploadField}
                  />
                </div>
              ))
            )}
            <div className="space-y-1.5">
              <Label>Free-form note (optional)</Label>
              <Textarea
                rows={3}
                placeholder="Anything else the creator should know."
                value={(brief["__note"] as string) ?? ""}
                onChange={e => setBrief(b => ({ ...b, __note: e.target.value }))}
              />
            </div>
            <div className="space-y-1.5">
              <Label>Requested deadline (optional)</Label>
              <Input type="date" value={deadline} onChange={e => setDeadline(e.target.value)} />
            </div>
          </CardContent>
        </Card>

        {(service.addons ?? []).length > 0 && (
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base">Add-ons</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              {(service.addons ?? []).map(a => {
                const active = addons.has(a.id);
                return (
                  <label
                    key={a.id}
                    className={[
                      "flex cursor-pointer items-start justify-between gap-3 rounded-md border p-3 transition",
                      active ? "border-foreground bg-accent" : "hover:bg-accent",
                    ].join(" ")}
                  >
                    <div className="flex items-start gap-3">
                      <Checkbox
                        checked={active}
                        onCheckedChange={v => {
                          setAddons(s => {
                            const n = new Set(s);
                            if (v) n.add(a.id); else n.delete(a.id);
                            return n;
                          });
                        }}
                      />
                      <div>
                        <p className="text-sm font-medium">{a.label}</p>
                        {a.description && (
                          <p className="text-xs text-muted-foreground">{a.description}</p>
                        )}
                      </div>
                    </div>
                    <span className="shrink-0 text-sm tabular-nums">
                      +{money(a.price_cents, service.currency)}
                    </span>
                  </label>
                );
              })}
            </CardContent>
          </Card>
        )}
      </form>

      <aside className="lg:sticky lg:top-20 lg:self-start">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Summary</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="rounded-md border bg-muted/30 p-3">
              <p className="text-xs uppercase tracking-wider text-muted-foreground">
                {isInstant ? "Instant price" : "Starts at"}
              </p>
              <p className="mt-1 text-3xl font-semibold tabular-nums">
                {money(livePrice, service.currency)}
              </p>
              {isInstant && (
                <p className="mt-1 text-xs text-muted-foreground">
                  Deposit due on submit: {money(liveDeposit, service.currency)} ({service.deposit_percent}%)
                </p>
              )}
            </div>
            <dl className="grid grid-cols-2 gap-y-2 text-sm">
              <dt className="text-muted-foreground">Delivery</dt>
              <dd className="text-right inline-flex items-center justify-end gap-1">
                <Clock className="h-3.5 w-3.5" /> ~{service.delivery_days} days
              </dd>
              <dt className="text-muted-foreground">Revisions</dt>
              <dd className="text-right">{service.revision_rounds} rounds</dd>
              <dt className="text-muted-foreground">Quote mode</dt>
              <dd className="text-right">
                <Badge variant={isInstant ? "default" : "secondary"} className="font-normal">
                  {isInstant ? "Instant" : "Manual"}
                </Badge>
              </dd>
            </dl>

            {service.revision_policy && (
              <Alert>
                <AlertTitle className="text-sm">Revision policy</AlertTitle>
                <AlertDescription className="text-xs">{service.revision_policy}</AlertDescription>
              </Alert>
            )}

            {!isInstant && (
              <Alert>
                <Hourglass className="h-4 w-4" />
                <AlertTitle className="text-sm">Pending quote</AlertTitle>
                <AlertDescription className="text-xs">
                  The creator will review your brief and reply with a price and ETA before any payment.
                </AlertDescription>
              </Alert>
            )}

            <Button
              type="submit"
              className="w-full gap-2"
              disabled={submitting}
              onClick={onSubmit as unknown as () => void}
            >
              {submitting
                ? "Submitting…"
                : isInstant
                  ? <>Submit & pay deposit <ArrowRight className="h-4 w-4" /></>
                  : <>Submit request <ArrowRight className="h-4 w-4" /></>}
            </Button>
            <p className="flex items-start gap-2 text-xs text-muted-foreground">
              <CheckCircle2 className="mt-0.5 h-3.5 w-3.5 shrink-0 text-emerald-500" />
              You'll track status, approvals, revisions, and delivery in your dashboard.
            </p>
          </CardContent>
        </Card>
      </aside>
    </div>
  );
}
